import { PostPopups } from 'app/modules/posts/modules/post/hooks/usePostPopups';
import { useUpdateApprovalPostStatus } from 'app/modules/posts/modules/detail/hooks/useUpdateApprovalPostStatus';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { modalRoutes } from 'constants/routes';
import { createSearchFromParams } from 'utils/url';
import { useModalHistory } from 'context/ModalRouter';
import { PageData } from 'types/Page';
import { PostsAddNewProfileReason } from 'app/modules/posts/components/postsAddNewProfileModal/hooks/useAddNewProfileReasonMessages';
import { CommentThread } from 'app/modules/comments/types';
import { useTranslation } from 'react-i18next';
import { useHistoryQueryParams } from 'app/hooks/useHistoryQueryParams';
import PostsConfig from 'app/config/posts';
import { RefObject } from 'react';
import { PostCommunicationRef } from '../components/postCommunication/PostCommunicationProvider';
import { ScheduleMethodType } from 'app/modules/posts/types/ScheduleMethodType';

export const usePostDetailWorkflowHandlers = (
  postId: number,
  showPopup: (popup: PostPopups) => void,
  postCommunicationRef: RefObject<PostCommunicationRef>,
  page?: PageData,
) => {
  const modalHistory = useModalHistory();
  const queryParamHistory = useHistoryQueryParams();
  const updateApprovalStatus = useUpdateApprovalPostStatus({ postId });
  const { t } = useTranslation();

  const onRespondToApprovalRequest = async (
    approvalType: 'approve' | 'rework',
    thread: CommentThread,
  ) => {
    const options = {
      approve: {
        text: t('commentsWorkflowMessages.approve'),
        action: thread === 'client' ? 'client_approval' : 'internal_approval',
      },
      rework: {
        text: t('commentsWorkflowMessages.rework'),
        action: thread === 'client' ? 'client_rework' : 'internal_rework',
      },
    } as const;

    queryParamHistory.setQueryParameter(
      PostsConfig.COMMUNICATION_TAB_QUERY_PARAM_KEY,
      thread,
    );

    switch (approvalType) {
      case 'approve':
        postCommunicationRef.current?.resetCommentAction();
        updateApprovalStatus.updateThroughComment.mutate({
          thread: thread,
          text: options[approvalType].text,
          action: options[approvalType].action,
        });
        break;
      case 'rework':
        setTimeout(() => {
          postCommunicationRef.current?.initReworkCommentAction(
            thread as Exclude<CommentThread, 'pinned'>,
          );
        }, 500);
        break;
    }
  };

  return {
    onChangeToDraft: () =>
      updateApprovalStatus.updateManually.mutate({ status: STATUS.COMPLETED }),
    onAssignTask: () => showPopup(PostPopups.AssignTask),
    onSchedule: (type?: ScheduleMethodType) => {
      if (page?.isDemo) {
        modalHistory.push({
          hash: modalRoutes.NEW_PROFILE,
          search: `?reason=${PostsAddNewProfileReason.demo}`,
        });
        return;
      }

      switch (type) {
        case 'publishNow':
          showPopup(PostPopups.SchedulePublishNow);
          break;
        case 'createDarkPost':
          showPopup(PostPopups.ScheduleDarkPost);
          break;
        case 'exportInAdvance':
          showPopup(PostPopups.ScheduleExportInAdvance);
          break;
        default:
          showPopup(PostPopups.Schedule);
          break;
      }
    },
    onMarkAsDone: () =>
      updateApprovalStatus.updateManually.mutate({
        status: STATUS.DONE,
        markAsDone: true,
      }),
    onSendForInternalApproval: () => showPopup(PostPopups.InternalApproval),
    onSendForClientApproval: () => showPopup(PostPopups.ClientApproval),
    onRespondToApprovalRequest,
    onSharePublicLink: () => {
      modalHistory.push({
        hash: modalRoutes.SHARE_PREVIEW,
        search: createSearchFromParams({ id: postId }),
      });
    },
  };
};
